import { AxiosError } from "axios";
import { paymentCancellationReasons } from "../constants/payments-erros";
import { type ApiError } from "../api/types";

export const ApiErrorType = {
  PAYMENT_CANCELLED: "PAYMENT_CANCELLED",
  ACCESS_DENIED: "ACCESS_DENIED",
  RESOURCE_CONFLICT: "RESOURCE_CONFLICT",
  SERVER_ERROR: "SERVER_ERROR",
  NOT_FOUND: "NOT_FOUND",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  NETWORK_ERROR: "NETWORK_ERROR",
  UNKNOWN: "UNKNOWN",
} as const;

export type ApiErrorType = (typeof ApiErrorType)[keyof typeof ApiErrorType];

export interface ClassifiedError {
  type: ApiErrorType;
  status?: number;
  message: string;
  originalError: unknown;
}

interface ApiErrorResponse {
  error?: ApiError;
  message?: string;
  [key: string]: unknown;
}

function isAxiosError(error: unknown): error is AxiosError<ApiErrorResponse> {
  return (error as AxiosError).isAxiosError === true;
}

/**
 * 503 DATA_PREPARING — данные кабинета (каталог артикулов) ещё собираются.
 * Это не сбой: показываем «собирается» и опрашиваем, а не «ошибка» (SAEX-3438).
 */
export function isDataPreparingError(error: unknown): boolean {
  // query.state.error до первой ошибки — null, а isAxiosError его не переживает.
  if (error == null || !isAxiosError(error)) return false;
  if (error.response?.status !== 503) return false;
  const apiError = error.response.data?.error;
  return typeof apiError === "object" && apiError?.code === "DATA_PREPARING";
}

/**
 * Достаёт текст ошибки от бэкенда, а если его нет — возвращает фолбэк.
 *
 * В отличие от classifyApiError не подменяет сообщение своими формулировками:
 * нужен там, где у вызывающего есть собственный текст на случай, когда сервер
 * ничего внятного не сказал.
 */
export function getServerMessage(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    return getErrorMessage(error.response?.data) || fallback;
  }
  return fallback;
}

function getErrorMessage(data: unknown): string {
  if (typeof data === "object" && data !== null) {
    const errorData = data as ApiErrorResponse;
    return (
      errorData.message ||
      (typeof errorData.error === "string" ? errorData.error : "")
    );
  }
  return "";
}

export function classifyApiError(error: unknown): ClassifiedError {
  if (!error) {
    return {
      type: ApiErrorType.UNKNOWN,
      message: "Unknown error occurred",
      originalError: error,
    };
  }

  if (isAxiosError(error)) {
    const status = error.response?.status;
    const responseData = error.response?.data;
    const apiError = responseData?.error;
    const serverMessage = getErrorMessage(responseData);

    if (apiError?.code === "PAYMENT_CANCELLED") {
      const reason = apiError.data?.cancellation_reason || "default";
      const message =
        paymentCancellationReasons[reason] ||
        paymentCancellationReasons.default;
      return {
        type: ApiErrorType.PAYMENT_CANCELLED,
        status,
        message,
        originalError: error,
      };
    }

    if (!status) {
      return {
        type: ApiErrorType.NETWORK_ERROR,
        message: "Проблема с сетью. Проверьте подключение",
        originalError: error,
      };
    }

    switch (status) {
      case 401:
      case 403:
        return {
          type: ApiErrorType.ACCESS_DENIED,
          status,
          message: serverMessage || "Доступ запрещен",
          originalError: error,
        };

      case 404:
        return {
          type: ApiErrorType.NOT_FOUND,
          status,
          message: serverMessage || "Ресурс не найден",
          originalError: error,
        };

      case 409:
        return {
          type: ApiErrorType.RESOURCE_CONFLICT,
          status,
          message: serverMessage || "Ресурс уже существует",
          originalError: error,
        };

      case 400:
        return {
          type: ApiErrorType.VALIDATION_ERROR,
          status,
          message: serverMessage || "Некорректные данные",
          originalError: error,
        };

      case 500:
      case 502:
      case 503:
        return {
          type: ApiErrorType.SERVER_ERROR,
          status,
          message: "Сервер временно недоступен. Попробуйте позже",
          originalError: error,
        };

      default:
        return {
          type: ApiErrorType.UNKNOWN,
          status,
          message: serverMessage || "Произошла ошибка",
          originalError: error,
        };
    }
  }

  return {
    type: ApiErrorType.UNKNOWN,
    message: error instanceof Error ? error.message : "Произошла ошибка",
    originalError: error,
  };
}

export function getUserFriendlyMessage(
  classifiedError: ClassifiedError,
  context?: { email?: string; tableName?: string },
): string {
  if (classifiedError.type === ApiErrorType.PAYMENT_CANCELLED) {
    return classifiedError.message;
  }

  switch (classifiedError.type) {
    case ApiErrorType.ACCESS_DENIED:
      return context?.email
        ? `Предоставьте доступ к таблице для email: ${context.email}`
        : "Недостаточно прав для выполнения операции";

    case ApiErrorType.RESOURCE_CONFLICT:
      return context?.tableName
        ? `${context.tableName} уже подключена`
        : "Ресурс уже существует";

    case ApiErrorType.SERVER_ERROR:
      return "Сервер временно недоступен. Попробуйте позже или обратитесь в поддержку";

    case ApiErrorType.NETWORK_ERROR:
      return "Проблема с подключением. Проверьте интернет-соединение";

    case ApiErrorType.VALIDATION_ERROR:
      return (
        classifiedError.message || "Проверьте правильность введенных данных"
      );

    case ApiErrorType.NOT_FOUND:
      return "Запрашиваемый ресурс не найден";

    default:
      return "Произошла непредвиденная ошибка. Попробуйте еще раз";
  }
}
